// @flow
/**
 * User: Mike Chung
 * Date: 3/3/2018
 * Time: 11:59 PM
 */
import EBadge from "../model/ebadge/EBadge";
import * as queryString from "query-string";
import config from "../config/config";
import type {Caller} from "../components/QueryPage";
import * as _ from "lodash";
import type {PhotoEBadgeSearchCriteria} from "../components/ebadge/photo/PhotoEBadgeSearchForm";
import type {PhotoEBadgeQueryResultType} from "../components/ebadge/photo/PhotoEBadgeQueryResult";
import {isEmpty} from "lodash";
import type {SortOption} from "../model/ebadge/EBadgeQueryCriteria";

type EBadgeQuery = {
    registrationCode?: string,
    firstName?: string,
    lastName?: string,
    email?: string,
    companyName?: string,
    fairConfig?: string,
    page: number,
    pageItem: number,
    sort?: SortOption,
    direction?: 'asc' | 'desc',
    projectCode?: string,
    year?: string,
    target?: 'email',
    ebadgeType?: string,
    boothNumber?: string,
    sourceTypes?: Array<string>,
    visitorTypes?: Array<string>,
}

type EBadgeQueryResult = {
    "totalResult": number,
    "result": Array<EBadge>
}

export async function findEBadgeByCriteria(criteria: EBadgeQuery, auth: string): Promise<EBadgeQueryResult> {

    let query = {
        ...criteria
    };
    const omitIfEmpty = (o) => !("" + o);
    let keys = _.chain(criteria).omitBy(omitIfEmpty).omit(['page', 'pageItem', 'sort', 'direction', 'cQw08la1wX', 'target']).keys().value();

    const mandatoryFields: Array<string> = ["registrationCode"];
    const mandatoryFields2: Array<string> = ["year"];
    const mandatoryFields3: Array<string> = ["projectCode"];

    if (!(criteria.target && keys.length === 1 && keys[0] === criteria.target) &&
        (_.intersection(keys, mandatoryFields).length === 0)) {
            if ((_.intersection(keys, mandatoryFields2).length === 0) || (_.intersection(keys, mandatoryFields3).length === 0)){
                throw "Doesn't match the mandatory fields";
            }
    }

    if (query.sort && query.direction === 'desc') {
        query.sort = `-${query.sort}`;
    }

    if (criteria.target && keys.length === 1) {
        query.searchMode = "exact";
    }

    let response = await fetch(`${config.apiGatewayProcotol}://${config.apiGatewayHostname}${config.apiGatewayContext}/ebadge-admin/v1/badges?${queryString.stringify(query)}`, {
        headers: {
            "content-type": "text/plain",
            "Authorization": `Bearer ${auth}`
        }
    });
    if (response.status === 200) {

        let result: EBadgeQueryResult = await response.json();
        return result;
    } else if (response.status === 400) {

        throw {};

    } else if (response.status === 403 || response.status === 401) {

        throw await response.json();


    } else if (response.status === 504) {

        throw await response.json();


    } else {
        throw new Error();
    }

}


export async function lockEBadge(registrationCode: string, caller: Caller, auth: string, boothNumber: ?string): Promise<void> {

    const obj: any = {
        registrationCodeList: [registrationCode],
        operatorInputName: caller.comment,
        boothNumber,
        ...caller
    };

    let response = await fetch(`${config.apiGatewayProcotol}://${config.apiGatewayHostname}${config.apiGatewayContext}/ebadge-admin/v1/badges/redeem-by-admin`, {
        method: "POST",
        headers: {
            "content-type": "application/json",
            "Authorization": `Bearer ${auth}`
        },
        body: JSON.stringify(obj)
    });

    if (response.status === 200) {
        const result = await response.json();
        if (!isEmpty(result.failed)) {
            throw `Failed to link ${registrationCode}. Error code: ${result.failed[0].errorCode}`
        }
        return result;

    } else if (response.status === 400) {

        const error = (await response.json());
        throw error.message;
    } else if (response.status === 403 || response.status === 401) {

        throw await response.json();

    } else if (response.status === 504) {

        throw await response.json();

    } else {
        throw new Error();
    }
}


export async function unlockEBadge(registrationCode: string, caller: Caller, auth: string): Promise<void> {

    const obj: any = {
        registrationCode,
        operatorInputName: caller.comment,
        ...caller
    };
    let response = await fetch(`${config.apiGatewayProcotol}://${config.apiGatewayHostname}${config.apiGatewayContext}/ebadge-admin/v1/badges/unlock-by-registration-code`, {
        method: "POST",
        headers: {
            "content-type": "application/json",
            "Authorization": `Bearer ${auth}`
        },
        body: JSON.stringify(obj)
    });

    if (response.status === 200) {
        return await response.json();

    } else if (response.status === 403 || response.status === 401) {

        throw await response.json();


    } else if (response.status === 504) {

        throw await response.json();

    } else {
        throw new Error();
    }
}

export async function findPhotoEBadgeByCriteria(criteria: PhotoEBadgeSearchCriteria, auth: string): Promise<PhotoEBadgeQueryResultType> {

    const page: number = criteria.page || 1;
    const pageItem: number = criteria.pageItem || 20;

    let obj: any = {...criteria};
    obj.offset = (page - 1) * pageItem;
    obj.limit = pageItem;

    if (obj.projectCode && _.isArray(obj.projectCode)) {
        if (_.isEmpty(obj.projectCode)) {
            obj = _.omit(obj, 'projectCode');
        } else {
            obj.projectCode = obj.projectCode.join(',');
        }
    }
    if (obj.sourceTypes) {
        if (_.isEmpty(obj.sourceTypes) || obj.sourceTypes === "all") {
            obj = _.omit(obj, 'sourceTypes');
        } else {
            if (_.isArray(obj.sourceTypes)) {
                if (_.some(obj.sourceTypes, s => (s === "all"))) {
                    obj = _.omit(obj, 'sourceTypes');
                } else {
                    obj.sourceTypes = obj.sourceTypes.join(',');
                }
            }
        }
    }

    if (obj.visitorTypes) {
        if (_.isEmpty(obj.visitorTypes) || obj.visitorTypes === "all") {
            obj = _.omit(obj, 'visitorTypes');
        } else {
            if (_.isArray(obj.visitorTypes)) {
                if (_.some(obj.visitorTypes, v => (v === "all"))) {
                    obj = _.omit(obj, 'visitorTypes');
                } else {
                    obj.visitorTypes = obj.visitorTypes.join(',');
                }
            }
        }
    }

    let response = await fetch(`${config.apiGatewayProcotol}://${config.apiGatewayHostname}/ebadge-admin/ebadge-photo/v1/photos?${queryString.stringify(obj)}`, {
        headers: {
            "content-type": "text/plain",
            "Authorization": `Bearer ${auth}`
        }
    });
    if (response.status === 200) {

        let result: PhotoEBadgeQueryResultType = await response.json();
        return result;
    } else if (response.status === 400) {

        throw new Error("Your search criteria is incorrect.");

    } else if (response.status === 403 || response.status === 401) {

        throw await response.json();


    } else if (response.status === 504) {

        throw await response.json();


    } else {
        throw new Error("Unexpected Error");
    }
}

export async function deleteEBadgePhoto(registrationCode: string, caller: Caller, auth: string): Promise<any> {

    const obj: any = {
        deletedBy: `${caller.operator}_${caller.comment}`
    };
    let response = await fetch(`${config.apiGatewayProcotol}://${config.apiGatewayHostname}/ebadge-admin/ebadge-photo/v1/photos/${registrationCode}?${queryString.stringify(obj)}`, {
        method: "DELETE",
        headers: {
            "content-type": "application/json",
            "Authorization": `Bearer ${auth}`
        }
    });

    if (response.status === 204) {
        return true;

    } else if (response.status === 403 || response.status === 401) {

        throw await response.json();

    } else if (response.status === 504) {

        throw await response.json();

    } else {
        throw new Error("Unexpected Error");
    }
}

export async function bulkDeleteEBadgePhoto(registrationCodes: Array<string>, caller: Caller, auth: string): Promise<any> {

    const obj: any = {
        deletePhoto: _.map(registrationCodes, (registrationCode: string) => ({registrationCode})),
        deletedBy: `${caller.operator}_${caller.comment}`
    };

    let response = await fetch(`${config.apiGatewayProcotol}://${config.apiGatewayHostname}/ebadge-admin/ebadge-photo/v1/photos/bulk-delete`, {
        method: "POST",
        headers: {
            "content-type": "application/json",
            "Authorization": `Bearer ${auth}`
        },
        body: JSON.stringify(obj)
    });

    if (response.status === 200) {
        return true;

    } else if (response.status === 400) {

        throw await response.json();


    } else if (response.status === 403 || response.status === 401) {

        throw await response.json();

    } else if (response.status === 500) {

        throw await response.json();

    } else {
        throw new Error("Unexpected Error");
    }
}


export async function changeEBadgeVerificationStatus(registrationCode: string, caller: Caller, auth: string,
                                                     newStatus: "Y" | "N"): Promise<void> {

    const obj: any = {
        registrationCode,
        verifiedBy: caller.operator + "_" + caller.comment,
        verifyStatus: newStatus
    };

    let response = await fetch(`${config.apiGatewayProcotol}://${config.apiGatewayHostname}${config.apiGatewayContext}/ebadge-admin/v1/badges/change-verification-status`, {
        method: "POST",
        headers: {
            "content-type": "application/json",
            "Authorization": `Bearer ${auth}`
        },
        body: JSON.stringify(obj)
    });

    if (response.status === 200) {
        return await response.json();

    } else if (response.status === 410) {
        throw `The eBadge is already ${newStatus === 'Y' ? "validated" : "invalidated"}`

    } else if (response.status === 403 || response.status === 401) {

        throw await response.json();

    } else if (response.status === 504) {

        throw await response.json();

    } else {
        throw new Error();
    }
}

export async function changePhotoVerificationStatus(registrationCode: string, caller: Caller, auth: string,
                                                     newStatus: "Y" | "N"): Promise<void> {

    const obj: any = {
        registrationCode,
        verifiedBy: caller.operator + "_" + caller.comment,
        verifyStatus: newStatus
    };

    let response = await fetch(`${config.apiGatewayProcotol}://${config.apiGatewayHostname}${config.apiGatewayContext}/ebadge-admin/ebadge-photo/v1/photos/change-verification-status`, {
        method: "POST",
        headers: {
            "content-type": "application/json",
            "Authorization": `Bearer ${auth}`
        },
        body: JSON.stringify(obj)
    });

    if (response.status === 204) {
        return Promise.resolve();

    } else if (response.status === 410) {
        throw `The photo is already ${newStatus === 'Y' ? "validated" : "invalidated"}`
    } else if (response.status === 403 || response.status === 401) {

        throw await response.json();

    } else if (response.status === 504) {

        throw await response.json();

    } else {
        throw new Error();
    }
}


// WEBPACK FOOTER //
// ./src/dao/eBadgeDao.js